import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

// ★ 哨兵值住在 `acqService`(与 `UNASSIGNED_ASSIGNEE_ID` / `ICP_TENANT_SCOPE` 同一处):
//   它是**线上的约定值**,不是这个 hook 的私产;桩也要读它,而桩不该依赖 react-router。
export { ACQ_PRODUCT_NONE } from "../api/acqService";

/**
 * 四页共用的那一个产品参数:`?product=<node_code>`(契约 §16.7)。
 *
 * ★★ 为什么要进 URL 而不是只留在 state:产品是这一舱的主轴,四页之间要能**互相跳着带过去**
 *   ——「我的生意」左栏点「找这个产品的商机 →」落到去哪里找、今日的卡上点产品 chip
 *   切到该产品的页签、再把地址发给同事看到的是同一屏。留在组件 state 里的话,
 *   每跳一次就回到「全部」,而用户以为自己跳丢了。
 * ★ 空串 = 全部。**URL 里不留 `?product=`** —— 一个空值参数会被当成"筛过了"
 *   (分享出去的链接看起来像选了什么),而它什么也没筛。
 * ★ `replace: true`:切页签是**筛**不是**走**。不 replace 的话,用户在四个产品之间点五下,
 *   要按五次返回键才回得到上一页。
 * ★ 只动 `product` 这一个键,别的查询参数(将来的 tab / id)原样留着。
 */
export const ALL_PRODUCTS = "";

export function useProductParam(): [string, (code: string) => void] {
  const [params, setParams] = useSearchParams();
  const product = params.get("product")?.trim() ?? ALL_PRODUCTS;

  const setProduct = useCallback(
    (code: string) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (code) next.set("product", code);
          else next.delete("product");
          return next;
        },
        { replace: true }
      );
    },
    [setParams]
  );

  return useMemo(() => [product, setProduct], [product, setProduct]);
}

/**
 * 带着当前产品跳到另一页(契约 §16.7「四页互相跳转时带着」)。
 * ★ 没选产品就**不拼那个参数**,不拼成 `?product=`(见上面那条)。
 */
export function withProduct(path: string, code: string): string {
  return code ? `${path}?product=${encodeURIComponent(code)}` : path;
}
